import React, { useContext, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Phone, X, Loader2, RefreshCw } from "lucide-react";
import useModal from "../../hooks/use-modal";
import CustomPinInput from "../../components/custom-pin-input/custom-pin-input";
import API from "../../utils/api";
import { MainStore } from "../../stores/Main";

const ModalVerification = ({ modalProps }) => {
  const mainStore = useContext(MainStore);
  const { closeModal } = useModal();
  const [verCode, setVerCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [allowResend, setAllowResend] = useState(false);
  const [codeError, setCodeError] = useState(false);
  const pinInputRef = useRef();

  // Animation variants
  const contentAnimation = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, staggerChildren: 0.1 },
    },
  };

  const handleVerify = async () => {
    if (verCode.length !== 6) return;

    try {
      setLoading(true);
      setCodeError(false);
      const response = await API.post("/verification", {
        code: verCode,
        method: "phone",
      });
      if (response.data.result_code === 102) {
        setCodeError(true);
        if (pinInputRef.current?.clear) pinInputRef.current.clear();
        setVerCode("");
      } else if (response.data.result_code === 1) {
        setAllowResend(false);
        setTimeout(() => setAllowResend(true), 15000);
      } else {
        mainStore.handlePhoneVerified();
        modalProps.verifiedCode();
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setAllowResend(false);
    setCodeError(false);
    if (pinInputRef.current?.clear) pinInputRef.current.clear();
    setVerCode("");
    try {
      setLoading(true);
      await API.post("/verification", { method: "phone" });
      setTimeout(() => setAllowResend(true), 15000);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  // Send verification code on load
  useEffect(() => {
    handleResend();
    // Store ref globally for CustomPinInput compatibility
    window.pinInputRef = pinInputRef.current || {};
  }, []);

  return (
    <div className="flex flex-col h-full max-h-[100vh] sm:max-h-[90vh] p-0">
      {/* Modal Header */}
      <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 flex-shrink-0 sm:rounded-t-2xl">
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors group z-10"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
        </button>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-3 sm:gap-4 pr-10"
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
            <Phone className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">
              Phone Verification
            </h2>
            <p className="text-blue-100 text-sm sm:text-base">
              Verify your phone number
            </p>
          </div>
        </motion.div>
      </div>

      {/* Modal Body */}
      <motion.div
        className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 min-h-0 overscroll-contain bg-gray-900"
        variants={contentAnimation}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-md mx-auto">
          {/* Instructions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-center mb-8"
          >
            <p className="text-white/80 mb-2">
              Please enter the verification code that was sent to your phone.
            </p>
            <p className="text-white/60 text-sm">
              The code is valid for 10 minutes.
            </p>
          </motion.div>

          {/* Custom PIN Input */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <CustomPinInput
              ref={pinInputRef}
              length={6}
              onChange={(value) => {
                setVerCode(value);
                setCodeError(false);
              }}
              onComplete={(value) => {
                setVerCode(value);
                if (value.length === 6) handleVerify();
              }}
              disabled={loading}
              error={codeError}
            />
          </motion.div>

          {/* Error Message */}
          {codeError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-center"
            >
              <p className="text-red-400 text-sm">
                Invalid verification code. Please try again.
              </p>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-3"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={handleVerify}
              disabled={loading || verCode.length !== 6}
              className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold rounded-xl shadow-lg disabled:shadow-none transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify"
              )}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={closeModal}
              disabled={loading}
              className="w-full py-3 px-6 bg-white/[0.06] hover:bg-white/[0.1] border border-white/10 text-white font-semibold rounded-xl transition-all"
            >
              Cancel
            </motion.button>
          </motion.div>

          {/* Resend Code */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-6 text-center"
          >
            {allowResend ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleResend}
                className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Send new verification code
                </span>
              </motion.button>
            ) : (
              <p className="text-white/40 text-sm">
                Please wait before resending
              </p>
            )}
          </motion.div>
        </div>
      </motion.div>

      {/* Add shake animation for error state */}
      <style jsx>{`
        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-5px);
          }
          75% {
            transform: translateX(5px);
          }
        }
        .animate-shake {
          animation: shake 0.3s linear;
        }
      `}</style>
    </div>
  );
};

export default ModalVerification;
